import {pathToURL} from 'BenchlingUtil';
import {HydratedProperties} from 'data-frames/DataFrameTypes';
import {apiIdentifiedObjectToReadable, getHydratedBlob} from 'data-frames/util/getHydratedProperty';
import {WarehouseBlob} from 'schemas/tags/BlobConstants';
import {JSONValue} from 'util/Types';

export enum DataFrameValueResolutionMode {
  NONE = 'NONE',
  NAME = 'NAME',
  WAREHOUSE_BLOB_DICT = 'WAREHOUSE_BLOB_DICT',
}

export interface DataFrameResolutionConfig {
  apiIdentifiedObjects: DataFrameValueResolutionMode.NONE | DataFrameValueResolutionMode.NAME;
  blobs: DataFrameValueResolutionMode;
}

interface Resolver {
  canResolve: (
    value: JSONValue,
    hydratedProperties: HydratedProperties,
    resolutionConfig: DataFrameResolutionConfig
  ) => boolean;
  resolve: (value: JSONValue, hydratedProperties: HydratedProperties) => JSONValue;
}

// Only resolves accessible, hydrated blobs.
const BLOB_WAREHOUSE_DICT_RESOLVER: Resolver = {
  canResolve: (value, hydratedProperties, resolutionConfig) =>
    resolutionConfig.blobs === DataFrameValueResolutionMode.WAREHOUSE_BLOB_DICT &&
    getHydratedBlob(value, hydratedProperties) != null,
  resolve: (value: string, hydratedProperties): WarehouseBlob => {
    const {id, name, url} = getHydratedBlob(value, hydratedProperties)!;
    return {id, name, url: pathToURL(url)};
  },
};

// Only resolves accessible, hydrated blobs.
const BLOB_NAME_RESOLVER: Resolver = {
  canResolve: (value, hydratedProperties, resolutionConfig) =>
    resolutionConfig.blobs === DataFrameValueResolutionMode.NAME &&
    getHydratedBlob(value, hydratedProperties) != null,
  resolve: (value: string, hydratedProperties): string => getHydratedBlob(value, hydratedProperties)!.name,
};

// Resolves hydrated & inaccessible API identified objects.
const API_IDENTIFIED_OBJECT_NAME_RESOLVER: Resolver = {
  canResolve: (value, hydratedProperties, resolutionConfig) =>
    resolutionConfig.apiIdentifiedObjects === DataFrameValueResolutionMode.NAME &&
    apiIdentifiedObjectToReadable(value, hydratedProperties) != null,
  resolve: (value: string, hydratedProperties): string =>
    apiIdentifiedObjectToReadable(value, hydratedProperties)!,
};

const RESOLVERS: Array<Resolver> = [
  API_IDENTIFIED_OBJECT_NAME_RESOLVER,
  BLOB_NAME_RESOLVER,
  BLOB_WAREHOUSE_DICT_RESOLVER,
];

/**
 * Resolve an array of values.
 *
 * If all values are API IDs that resolve to names or blob UUIDs that resolve to names, the array will
 * resolve to a comma-delimited list of names.
 *
 * If all values are blob UUIDs that resolve to Warehouse Blob Dicts, the array will resolve to an array of
 * Warehouse Blob Dicts.
 *
 * If either blob UUIDs or API IDs are resolving to names, an empty list will resolve to an empty string.
 *
 * If not all values can be resolved the same way, the original array is returned. For example, an array
 * of API IDs and nulls will be returned unmodified.
 */
function resolveArray(
  values: Array<JSONValue>,
  hydratedProperties: HydratedProperties,
  resolutionConfig: DataFrameResolutionConfig
): JSONValue {
  if (values.length === 0) {
    return Object.values(resolutionConfig).includes(DataFrameValueResolutionMode.NAME) ? '' : values;
  }

  const resolver = RESOLVERS.find(({canResolve}) =>
    values.every((value) => canResolve(value, hydratedProperties, resolutionConfig))
  );

  if (resolver == null) {
    return values;
  }

  const resolvedValues = values.map((value) => resolver.resolve(value, hydratedProperties));
  return resolvedValues.every((value) => typeof value === 'string')
    ? resolvedValues.join(', ')
    : resolvedValues;
}

/**
 * Resolve a value from a data frame to a hydrated form, using the API IDs and blob dicts in
 * `hydratedProperties`.
 *
 * Depending on `resolutionConfig`, an API ID can resolve to a name and a blob UUID can resolve to a name
 * or a Warehouse Blob Dict.
 *
 * If the value is an array of API IDs, the resolved form is a comma-delimited list of names.
 * If the value is an array of blob UUIDs, the resolved form is a comma-delimited list of names or an array
 * of Warehouse Blob Dicts.
 *
 * If either blob UUIDs or API IDs are resolving to names, an empty list will resolve to an empty string.
 *
 * A resolved value may not be the same type of the original value, and cannot be described with the same
 * BenchlingTypeDef.
 *
 * @param value A value from a data frame.
 * @param hydratedProperties Hydrated properties from a data frame.
 * @param resolutionConfig How to resolve values.
 */
export default function resolveDataFrameValue(
  value: JSONValue,
  hydratedProperties: HydratedProperties,
  resolutionConfig: DataFrameResolutionConfig
): JSONValue {
  if (value instanceof Array) {
    return resolveArray(value, hydratedProperties, resolutionConfig);
  }
  const resolver = RESOLVERS.find(({canResolve}) => canResolve(value, hydratedProperties, resolutionConfig));
  return resolver ? resolver.resolve(value, hydratedProperties) : value;
}
